import type { TripLegConfidenceResponse } from '@/data/requests/trip-leg-confidence';
import type {
  LocationConfidence,
  TripLegConfidence,
  TripLegConfidenceF1ValidatePartial,
  TripLegAddressConfidenceF1ValidatePartial,
} from '@/data/models/trip-leg-confidence.model';

/**
 * Pure helpers for the trip-leg address-confidence (`/score/trip-leg`) ↔ legacy
 * `/validate` layering: classifying confidence errors, mapping them to form
 * fields, and merging successful /score confidence into the reservation payload so
 * `/validate` trusts the already-scored addresses instead of re-validating them.
 *
 * The orchestration that calls `/score/trip-leg` then `/validate` lives in
 * `runBundledValidation.ts`.
 */

/**
 * A trip-leg confidence error: either the body rejected by `/score/trip-leg`
 * (carries `errorCode`), or a `/validate` low-confidence error reclassified into
 * this channel (carries `messageId`).
 */
export type TripLegConfidenceErrorPayload = {
  message?: string;
  /** Present on /score/trip-leg errors, e.g. "03.01.254". */
  errorCode?: string;
  /** Present on /validate-sourced errors, e.g. "LOW_CONFIDENCE_ADDRESS". */
  messageId?: string;
  errorCodeData?: Record<string, unknown>;
  params?: Record<string, unknown>;
  /**
   * Form field this error maps to, resolved once at score time (see
   * `resolveConfidenceErrorFieldPath`) — NOT part of the API body. Both the red
   * field error and the yellow warning (field component) read this, so the
   * leg→field mapping lives in exactly one place.
   */
  formFieldPath?: string;
};

/**
 * What we keep per trip index after a /score/trip-leg response lands. The flags
 * record which side(s) were sent in the request so the merge step knows which
 * pieces of the response to apply to the reservation payload. Since the
 * endpoint started rejecting one-sided requests, legs are only scored with
 * both addresses present, so both flags are always true on new entries; they
 * are kept so the merge rules stay explicit.
 */
export type TripLegConfidenceStoredEntry = {
  response: TripLegConfidenceResponse;
  hasPickup: boolean;
  hasDropoff: boolean;
};

/**
 * Cache of a single leg's /score/trip-leg result keyed by its address payload,
 * so the bundled validation flow can skip re-scoring unchanged addresses across
 * field edits. Exactly one of `entry` / `error` is set. `error` holds the raw
 * response body — a `03.08.999` multi-error wrapper is cached as-is and only
 * expanded at consumption (see `expandTripLegScoreError`).
 */
export type TripLegScoreCacheEntry = {
  entry?: TripLegConfidenceStoredEntry;
  error?: TripLegConfidenceErrorPayload;
};

/**
 * /score/trip-leg's wrapper code for "multiple errors in one response" (e.g.
 * both addresses invalid). The individual errors ride in
 * `errorCodeData.errors`, each carrying `errorCategory`/`errorSpecific`
 * segments instead of a full `errorCode` string.
 */
export const TRIP_LEG_MULTI_ERROR_CODE = '03.08.999';

/** Shape of one entry in a `03.08.999` wrapper's `errorCodeData.errors`. */
type WrappedTripLegScoreError = {
  message?: string;
  params?: Record<string, unknown>;
  errorCategory?: string;
  errorSpecific?: string;
  errorCodeData?: Record<string, unknown>;
};

/**
 * Expands a /score/trip-leg error into the list of individual errors it
 * carries. A `03.08.999` wrapper's `errorCodeData.errors` are unpacked into
 * standalone payloads — each gets a full `errorCode` rebuilt as
 * `<domain>.<errorCategory>.<errorSpecific>` (domain from the wrapper code) so
 * `tripLegConfidenceErrorTranslationKey` resolves `errorCodes.<errorSpecific>`
 * exactly like a single-error response. Any other error — or a malformed
 * wrapper — passes through as a one-element list.
 */
export function expandTripLegScoreError(
  error: TripLegConfidenceErrorPayload,
): TripLegConfidenceErrorPayload[] {
  if (error?.errorCode !== TRIP_LEG_MULTI_ERROR_CODE) return [error];
  const innerErrors = (error.errorCodeData as { errors?: unknown } | undefined)
    ?.errors;
  if (!Array.isArray(innerErrors)) return [error];
  const domain = error.errorCode.split('.')[0];
  const expanded = innerErrors
    .filter(
      (inner): inner is WrappedTripLegScoreError =>
        inner != null && typeof inner === 'object',
    )
    .map(
      (inner): TripLegConfidenceErrorPayload => ({
        message: inner.message ?? error.message,
        params: inner.params,
        errorCode:
          inner.errorCategory && inner.errorSpecific
            ? `${domain}.${inner.errorCategory}.${inner.errorSpecific}`
            : undefined,
        errorCodeData: inner.errorCodeData,
      }),
    );
  return expanded.length > 0 ? expanded : [error];
}

// TODO(F1V2-4815 reconciliation): /validate requires `tripLegConfidenceLogID` on
// the confidence block, but /score/trip-leg doesn't return one. Sending `0` as a
// placeholder until either /score/trip-leg starts returning a real log ID or
// /validate drops the requirement.
const MISSING_TRIP_LEG_CONFIDENCE_LOG_ID_SENTINEL = 0;

/**
 * The i18n translation key for a trip-leg confidence error. /validate-sourced
 * errors carry a `messageId` (e.g. `LOW_CONFIDENCE_ADDRESS`) → `serverError.<id>`;
 * /score/trip-leg errors carry a backend `errorCode` like "03.01.254" →
 * `errorCodes.254` (last segment).
 */
export function tripLegConfidenceErrorTranslationKey(
  error: { messageId?: string; errorCode?: string } | undefined,
): string | null {
  if (error?.messageId) return `serverError.${error.messageId}`;
  const lastPart = error?.errorCode?.split('.').pop();
  return lastPart ? `errorCodes.${lastPart}` : null;
}

/**
 * Rewrites a confidence error's resolved `formFieldPath` — expressed in
 * reservationForm's tripSegmentSchema shape (`trips.N.pickupAddress` /
 * `trips.N.dropoffAddress`) — to the legacy react-hook-form field name
 * (`MultiLegTrips[N].PickupAddress` / `...DropoffAddress`) used by the admin-portal
 * `home/reservations/{ReservationDialog,TripLegDialog}.tsx`. Returns `null` for any
 * other path so callers skip it.
 */
function legacyAddressFieldFromSchemaPath(
  path: string | undefined,
): string | null {
  if (!path) return null;
  const match = path.match(
    /^trips[.[](\d+)]?\.(pickupAddress|dropoffAddress)$/,
  );
  if (!match) return null;
  const legacySide =
    match[2] === 'pickupAddress' ? 'PickupAddress' : 'DropoffAddress';
  return `MultiLegTrips[${match[1]}].${legacySide}`;
}

/**
 * Maps the trip-leg address-confidence errors collected by `runBundledValidation`
 * (`errorsByTripIndex`) onto the legacy admin-portal address form fields, returning
 * `{ field, message }` entries in the same shape as `reservationUtils.getFieldErrors`
 * so both can be applied through the dialogs' existing `setError` pipeline.
 *
 * The leg→field resolution — including the round-trip return-leg flip that collapses
 * both legs onto the single `trips.0` form row — is already baked into each error's
 * `formFieldPath` at score time (see `resolveConfidenceErrorFieldPath`). So this only
 * rewrites that path to the legacy field name and translates the message; no extra
 * round-trip handling is required here.
 *
 * `interpolationValues` supplies extra i18n interpolation values (e.g.
 * `errorCodes.256`'s {{clientName}}/{{clientNumber}} — see
 * `useClientMemberContact`); they win over same-named `errorCodeData` keys.
 */
export function getLegacyTripLegConfidenceFieldErrors(
  t: (key: string, options?: Record<string, unknown>) => string,
  errorsByTripIndex: Record<number, TripLegConfidenceErrorPayload[]>,
  interpolationValues?: Record<string, unknown>,
): { field: string; message: string }[] {
  const messages: { field: string; message: string }[] = [];
  for (const error of Object.values(errorsByTripIndex).flat()) {
    const field = legacyAddressFieldFromSchemaPath(error?.formFieldPath);
    if (!field) continue;
    const translationKey = tripLegConfidenceErrorTranslationKey(error);
    const message =
      (translationKey
        ? t(translationKey, {
            ...(error.errorCodeData ?? {}),
            ...(interpolationValues ?? {}),
            defaultValue: error.message ?? '',
          })
        : error.message) || '';
    if (!message) continue;
    messages.push({ field, message });
  }
  return messages;
}

/**
 * `/validate`'s `messageId` for a low address-confidence score — the canonical
 * low-confidence signal. The backend evaluates the (merged) confidence score
 * during /validate and flags it here; `runBundledValidation` routes it into the
 * trip-leg confidence channel so the client portal renders it as a non-blocking
 * warning and other portals as a blocking field error.
 */
export const LOW_CONFIDENCE_ADDRESS_MESSAGE_ID = 'LOW_CONFIDENCE_ADDRESS';

/**
 * Whether a trip-leg confidence error is the low address-confidence error,
 * detected by the /validate `messageId` (the same value whether read off the raw
 * /validate error or a stored `errorsByTripIndex` entry). Low confidence is the
 * only confidence error eligible for the client-portal warning treatment; every
 * other trip-leg error (e.g. invalid address) always blocks.
 */
export function isLowConfidenceError(
  error: { messageId?: string } | undefined,
): boolean {
  return error?.messageId === LOW_CONFIDENCE_ADDRESS_MESSAGE_ID;
}

/**
 * Whether a trip-leg confidence error is downgraded to a non-blocking *warning*
 * rather than a blocking error. Today that's client portal + low address
 * confidence: the reservation is allowed to proceed and the address is accepted.
 */
export function isWarningTripLegError(
  error: TripLegConfidenceErrorPayload | undefined,
  isClientPortal: boolean,
): boolean {
  return isClientPortal && isLowConfidenceError(error);
}

/**
 * Whether any trip-leg confidence error should block the /validate call and the
 * Next button. A confidence error blocks unless it's been downgraded to a warning
 * (see `isWarningTripLegError`). Every other error blocks, since skipping validate
 * means we can't know what other errors it would have surfaced.
 */
export function hasBlockingTripLegConfidenceError(
  errorsByTripIndex: Record<number, TripLegConfidenceErrorPayload[]>,
  isClientPortal: boolean,
): boolean {
  return Object.values(errorsByTripIndex)
    .flat()
    .some((err) => err && !isWarningTripLegError(err, isClientPortal));
}

/**
 * Which side of the trip leg a confidence error pertains to, read from
 * `errorCodeData.addressRole` (e.g. `"pickUp"` / `"dropOff"`). Newer responses
 * include it where the backend can attribute the failure to a specific address;
 * older ones omit it. Returns `undefined` when absent or unrecognized so callers
 * fall back to the long-standing drop-off assumption.
 */
function readErrorAddressRole(
  error: TripLegConfidenceErrorPayload | undefined,
): 'pickup' | 'dropoff' | undefined {
  const raw = (error?.errorCodeData as { addressRole?: unknown } | undefined)
    ?.addressRole;
  if (typeof raw !== 'string') return undefined;
  const role = raw.trim().toLowerCase();
  if (role.startsWith('pick')) return 'pickup';
  if (role.startsWith('drop')) return 'dropoff';
  return undefined;
}

/**
 * Whether a reservation leg is the synthesized **return** leg of a round trip —
 * detected from the leg's own `metadata.roundTripReturnLeg` (stamped by the
 * payload builders), not its position. Return legs mirror the outbound leg's
 * addresses and share the single visible form row (`trips.0`).
 */
export function isRoundTripReturnLeg(leg: unknown): boolean {
  return (
    (leg as { metadata?: { roundTripReturnLeg?: boolean } } | null | undefined)
      ?.metadata?.roundTripReturnLeg === true
  );
}

/**
 * The form field a trip-leg confidence error maps to.
 *
 * The side of the leg comes from the error's `errorCodeData.addressRole` when the
 * API provides it; otherwise we keep the original assumption that the error is
 * about the leg's **drop-off** address.
 *
 * A round-trip **return** leg has its addresses reversed relative to the single
 * form row (its pickup is the form's dropoff, its dropoff is the form's pickup) —
 * see `isRoundTripReturnLeg` — so the resolved side is flipped and pinned to
 * `trips.0`. One-way / multi-leg map to their own row's matching side.
 *
 * NOTE: a return leg's score errors are resolved like any other leg's, but
 * `runBundledValidation` only surfaces them when the outbound leg produced no
 * errors of its own (they duplicate the outbound feedback otherwise) — so the
 * flip branch matters exactly in that return-leg-only-failure case.
 *
 * This is the single source of truth for the leg→field mapping; resolved once at
 * score time and stamped onto the error (`formFieldPath`) for both consumers.
 */
export function resolveConfidenceErrorFieldPath(
  legIndex: number,
  leg: unknown,
  error?: TripLegConfidenceErrorPayload,
): string {
  const isReturnLeg = isRoundTripReturnLeg(leg);

  // Side of the trip leg the error is about; default to drop-off when the API
  // didn't say (backwards-compatible with the previous always-dropoff behavior).
  const legSide = readErrorAddressRole(error) ?? 'dropoff';

  if (isReturnLeg) {
    // Reversed addresses: the return leg's dropoff is the form's pickup and vice
    // versa, all on the single form row (trips.0).
    return legSide === 'dropoff'
      ? 'trips.0.pickupAddress'
      : 'trips.0.dropoffAddress';
  }

  return legSide === 'dropoff'
    ? `trips.${legIndex}.dropoffAddress`
    : `trips.${legIndex}.pickupAddress`;
}

function tripLegConfidenceFromResponse(
  tlc: TripLegConfidence,
): TripLegConfidenceF1ValidatePartial {
  return {
    tripLegConfidenceLogID: MISSING_TRIP_LEG_CONFIDENCE_LOG_ID_SENTINEL,
    isTripLegConfident: tlc.isTripLegConfident,
    tripLegConfidenceReasonID: tlc.tripLegConfidenceReasonID,
    tripLegConfidenceReasonBC: tlc.tripLegConfidenceReasonBC,
  };
}

function addressConfidenceFromLocation(
  loc: LocationConfidence,
): TripLegAddressConfidenceF1ValidatePartial {
  const parsed = parseFloat(loc.confidenceScore);
  return {
    locationConfidenceLogID: loc.locationConfidenceLogID,
    locationStatusID: loc.locationStatusID,
    confidenceScore: Number.isNaN(parsed) ? null : parsed,
    confidenceDetails: loc.confidenceDetails,
    locationStatusBC: loc.locationStatusBC,
  };
}

/**
 * /score/trip-leg historically returned location confidence in two shapes:
 *  - two-sided requests → nested under `tripLegConfidence` (`pickupLocationConfidence` / `dropOffLocationConfidence`),
 *  - one-sided requests → top-level on the response (`pickUpLocationConfidence` / `dropOffLocationConfidence`)
 *    with no `tripLegConfidence` wrapper.
 * The pickup field also has a casing inconsistency (`pickup…` nested vs `pickUp…` top-level).
 * One-sided requests are no longer sent (the endpoint rejects them), but the
 * normalization is kept defensively so the merge step doesn't care which shape
 * came back.
 */
function extractPickupLocationConfidence(
  response: TripLegConfidenceResponse,
): LocationConfidence | undefined {
  const top = response as unknown as {
    pickUpLocationConfidence?: LocationConfidence;
    pickupLocationConfidence?: LocationConfidence;
  };
  return (
    top.pickUpLocationConfidence ??
    top.pickupLocationConfidence ??
    response.tripLegConfidence?.pickupLocationConfidence
  );
}

function extractDropoffLocationConfidence(
  response: TripLegConfidenceResponse,
): LocationConfidence | undefined {
  const top = response as unknown as {
    dropOffLocationConfidence?: LocationConfidence;
  };
  return (
    top.dropOffLocationConfidence ??
    response.tripLegConfidence?.dropOffLocationConfidence
  );
}

/**
 * Merges stored trip-leg address confidence API responses into a bare tripLegs
 * array (the POST `/updateTripLegs` shape). `confidence` / `confidenceScore` are
 * optional on the backend contract. Per-leg rules:
 *  - emit `pickUpAddress.confidenceScore` only when pickup was sent in the original /score/trip-leg request,
 *  - emit `dropOffAddress.confidenceScore` only when dropoff was sent,
 *  - emit leg-level `confidence` only when BOTH sides were sent (the trip-leg confidence is meaningless
 *    for a one-sided request).
 */
export function mergeTripLegConfidenceResponsesIntoTripLegs(
  tripLegs: unknown[],
  confidenceByTripIndex: Record<number, TripLegConfidenceStoredEntry>,
): unknown[] {
  if (Object.keys(confidenceByTripIndex).length === 0) {
    return tripLegs;
  }
  return tripLegs.map((leg, idx) => {
    const entry = confidenceByTripIndex[idx];
    if (!entry || typeof leg !== 'object' || leg === null) return leg;
    const { response, hasPickup, hasDropoff } = entry;
    const tlc = response.tripLegConfidence;
    const legObj = leg as Record<string, unknown>;
    const pickup = legObj.pickUpAddress as Record<string, unknown> | undefined;
    const dropoff = legObj.dropOffAddress as
      | Record<string, unknown>
      | undefined;
    const pickupLoc = extractPickupLocationConfidence(response);
    const dropoffLoc = extractDropoffLocationConfidence(response);
    const emitLegConfidence = hasPickup && hasDropoff && tlc != null;
    const emitPickupScore = hasPickup && pickup != null && pickupLoc != null;
    const emitDropoffScore =
      hasDropoff && dropoff != null && dropoffLoc != null;
    return {
      ...legObj,
      ...(emitLegConfidence
        ? { confidence: tripLegConfidenceFromResponse(tlc) }
        : {}),
      ...(emitPickupScore
        ? {
            pickUpAddress: {
              ...pickup,
              confidenceScore: addressConfidenceFromLocation(pickupLoc),
            },
          }
        : {}),
      ...(emitDropoffScore
        ? {
            dropOffAddress: {
              ...dropoff,
              confidenceScore: addressConfidenceFromLocation(dropoffLoc),
            },
          }
        : {}),
    };
  });
}

/**
 * Merges stored trip-leg address confidence API responses into the reservation
 * body for POST `/validate` / `/createReservation`. Same per-leg rules as
 * `mergeTripLegConfidenceResponsesIntoTripLegs`.
 */
export function mergeTripLegConfidenceResponsesIntoReservationPayload(
  reservation: unknown,
  confidenceByTripIndex: Record<number, TripLegConfidenceStoredEntry>,
): unknown {
  if (
    typeof reservation !== 'object' ||
    reservation === null ||
    Object.keys(confidenceByTripIndex).length === 0
  ) {
    return reservation;
  }
  const r = reservation as { tripLegs?: unknown[] };
  if (!Array.isArray(r.tripLegs)) {
    return reservation;
  }
  return {
    ...r,
    tripLegs: mergeTripLegConfidenceResponsesIntoTripLegs(
      r.tripLegs,
      confidenceByTripIndex,
    ),
  };
}
